import * as moment from 'moment';

import { ListingsAppointments } from '@appointments/models/appointments/listings-appointments';
import { ListingsComments } from '@comments/models/comments/listings-comments';
import { IExtendedFilterOptions } from '@core-models/filter/extended-filter-options-interface';
import { FilterOptionsBase } from '@core-models/filter/filter-options-base';
import { ListingActivities } from '@listings/enums/listing-activities.enum';
import { ListingCategoryTexts } from '@listings/enums/listing-category-texts.enum';
import { CommonListing } from '@listings/models/listing/common-listing';
import { MyListingsFilters } from '@my-listings/models/filter/my-listings-filters.enum';
import { LayoutFilterSettingsConstants } from '@settings/store/constants.ts/layout-filter-settings.constants';

export class MyListingsFilterOptions extends FilterOptionsBase implements IExtendedFilterOptions<CommonListing, ListingsAppointments, ListingsComments> {
    [MyListingsFilters.category] = ListingCategoryTexts.Sales;
    [MyListingsFilters.piked] = false;
    [MyListingsFilters.liked] = false;
    [MyListingsFilters.disliked] = false;
    [MyListingsFilters.shown] = false;
    [MyListingsFilters.newMatch] = false;
    [MyListingsFilters.newlyAdded] = false;
    [MyListingsFilters.upcomingOpenHouseStartDate]: Date = null;
    [MyListingsFilters.upcomingOpenHouseEndDate]: Date = null;
    [MyListingsFilters.upcomingAppointmentStartDate]: Date = null;
    [MyListingsFilters.upcomingAppointmentEndDate]: Date = null;
    [MyListingsFilters.usersFilter]: string = LayoutFilterSettingsConstants.allUsers;
    [MyListingsFilters.hasAppointments] = false;
    [MyListingsFilters.hasComments] = false;

    public static getDefaultFilter(): MyListingsFilterOptions {
        return new MyListingsFilterOptions();
    }

    public static getNewMatchFilter(): MyListingsFilterOptions {
        const filter = new MyListingsFilterOptions();
        filter.newMatch = true;
        return filter;
    }

    public static getCommentsFilter(): MyListingsFilterOptions {
        const filter = new MyListingsFilterOptions();
        filter.hasComments = true;
        return filter;
    }

    public static getAppointmentsFilter(): MyListingsFilterOptions {
        const filter = new MyListingsFilterOptions();
        filter.hasAppointments = true;
        return filter;
    }

    public static getLikedFilter(): MyListingsFilterOptions {
        const filter = new MyListingsFilterOptions();
        filter.liked = true;
        return filter;
    }

    // eslint-disable-next-line complexity
    public filter(collection: CommonListing[], appointments: ListingsAppointments = null, comments: ListingsComments = null): CommonListing[] {
        if (collection == null) {
            return [];
        }

        const filterFunctions: ((listing: CommonListing, appointments: ListingsAppointments, comments: ListingsComments) => boolean)[] = [];

        // category filter
        filterFunctions.push((listing: CommonListing) => listing.category === this.category.toLowerCase());

        // from filter
        if (this.usersFilter !== null && this.usersFilter !== LayoutFilterSettingsConstants.allUsers) {
            const selectedFrom = this.usersFilter.split(',');

            if (selectedFrom.length > 0) {
                filterFunctions.push((listing: CommonListing) =>
                    selectedFrom.includes(listing.listingOwnerId.toString()) && !listing.isNewMatch
                );
            }
        }

        // activities & newMatch filter
        const selectedActivities = [
            { activity: ListingActivities.Picked, selected: this.piked },
            { activity: ListingActivities.Liked, selected: this.liked },
            { activity: ListingActivities.Disliked, selected: this.disliked },
            { activity: ListingActivities.Shown, selected: this.shown }
        ].filter(activity => activity.selected).map(selectedActivity => selectedActivity.activity);

        if (selectedActivities.length > 0 || this.newMatch) {
            filterFunctions.push((listing: CommonListing) =>
                listing.activities.some(activity => selectedActivities.some(a => activity.id === a)) || (this.newMatch && listing.isNewMatch)
            );
        }

        if (this.newlyAdded) {
            filterFunctions.push((listing: CommonListing) => !listing.isNewViewed);
        }

        if (this.hasAppointments) {
            filterFunctions.push((listing: CommonListing, listingAppointments: ListingsAppointments) =>
                listingAppointments != null &&
                listingAppointments[listing.hashCode]?.length > 0
            );
        }

        // comments filter
        if (this.hasComments) {
            filterFunctions.push((listing: CommonListing, listingAppointments: ListingsAppointments, listingComments: ListingsComments) =>
                listingComments != null
                && listingComments[listing.hashCode] != null);
        }

        // open house filters
        if (this.upcomingOpenHouseStartDate != null && this.upcomingOpenHouseEndDate != null) {
            filterFunctions.push((listing: CommonListing) =>
                listing.openHouses.some(openHouse =>
                    moment(openHouse.startDate).isSameOrAfter(this.upcomingOpenHouseStartDate, 'day') &&
                    moment(openHouse.endDate).isSameOrBefore(this.upcomingOpenHouseEndDate, 'day'))
            );
        } else {
            if (this.upcomingOpenHouseStartDate != null) {
                filterFunctions.push((listing: CommonListing) =>
                    listing.openHouses.some(openHouse => moment(openHouse.startDate).isSameOrAfter(this.upcomingOpenHouseStartDate, 'day'))
                );
            }

            if (this.upcomingOpenHouseEndDate != null) {
                filterFunctions.push((listing: CommonListing) =>
                    listing.openHouses.some(openHouse => moment(openHouse.endDate).isSameOrBefore(this.upcomingOpenHouseEndDate, 'day'))
                );
            }
        }

        // upcoming appointmetns filter
        if (this.upcomingAppointmentStartDate != null && this.upcomingAppointmentEndDate != null) {
            filterFunctions.push((listing: CommonListing, listingAppointments: ListingsAppointments) =>
                listingAppointments != null && listingAppointments[listing.hashCode] != null &&
                listingAppointments[listing.hashCode].some(appointment =>
                    moment(appointment.startDateTime).isSameOrAfter(this.upcomingAppointmentStartDate, 'day') &&
                    moment(appointment.endDateTime).isSameOrBefore(this.upcomingAppointmentEndDate, 'day'))
            );
        } else {
            if (this.upcomingAppointmentStartDate != null) {
                filterFunctions.push((listing: CommonListing, listingAppointments: ListingsAppointments) =>
                    listingAppointments != null && listingAppointments[listing.hashCode] != null &&
                    listingAppointments[listing.hashCode].some(appointment =>
                        moment(appointment.startDateTime).isSameOrAfter(this.upcomingAppointmentStartDate, 'day'))
                );
            }

            if (this.upcomingAppointmentEndDate != null) {
                filterFunctions.push((listing: CommonListing, listingAppointments: ListingsAppointments) =>
                    listingAppointments != null && listingAppointments[listing.hashCode] != null &&
                    listingAppointments[listing.hashCode].some(appointment =>
                        moment(appointment.endDateTime).isSameOrBefore(this.upcomingAppointmentEndDate, 'day'))
                );
            }
        }

        return collection.filter(listing => filterFunctions.every(filter => filter(listing, appointments, comments)));
    }
}